<template>
  <div>
    <div class="list-toolbar">
      <el-select
        v-model="selectedManufacturerId"
        placeholder="Select manufacturer"
        size="small"
        filterable
        class="manufacturer-select"
      >
        <el-option
          v-for="m in safeManufacturers"
          :key="m.id"
          :label="m.name"
          :value="m.id"
        />
      </el-select>
      <el-input
        v-model="filter"
        size="small"
        prefix-icon="el-icon-search"
        placeholder="filter by code"
        clearable
        class="list-filter"
      ></el-input>
      <el-button
        size="small"
        icon="el-icon-refresh"
        :loading="loading"
        @click="getData"
        >Refresh</el-button
      >
    </div>
    <div class="app-card">
      <el-table
        border
        ref="table"
        :data="tableData"
        size="small"
        v-loading="loading"
        class="app-table"
        :max-height="tableHeight"
        :default-sort="{ prop: 'expiryDate', order: 'descending' }"
      >
        <el-table-column label="Subscription Code" prop="code" />
        <el-table-column
          label="Status"
          prop="status"
          width="120"
          :filters="[
            { text: 'Approved', value: 'Approved' },
            { text: 'Trial', value: 'Trial' },
            { text: 'Disabled', value: 'Disabled' }
          ]"
          :filter-method="filterHandler"
        >
          <template slot-scope="{ row }">
            <el-tag
              :type="statusTagType(row.status)"
              size="small"
              class="status-tag"
              >{{ row.status }}</el-tag
            >
          </template>
        </el-table-column>
        <el-table-column label="Expiry Date" prop="expiryDateTime" sortable>
          <template slot-scope="{ row }">
            {{
              row.expiryDateTime
                ? moment(row.expiryDateTime).format('DD/MM/YYYY @ hh:mm a')
                : '--'
            }}
          </template>
        </el-table-column>
        <el-table-column label="Created Date" prop="createdDateTime" sortable>
          <template slot-scope="{ row }">
            {{ moment(row.createdDateTime).format('DD/MM/YYYY @ hh:mm a') }}
          </template>
        </el-table-column>
        <el-table-column label="Assigned To" prop="assigedTo">
          <template>
            {{ selectedManufacturerName }}
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('text.action') | firstLetterCapitalize"
          min-width="160"
          fixed="right"
        >
          <template slot-scope="scope">
            <template v-if="scope.row.status !== 'Disabled'">
              <el-button
                size="small"
                type="primary"
                plain
                @click="edit(scope.row)"
                >Set Expiry</el-button
              >
              <el-button
                v-if="scope.row.status === 'Trial'"
                size="small"
                type="success"
                plain
                @click="approveCode(scope.row)"
                >Set Approved</el-button
              >
            </template>
            <el-button
              v-else
              size="small"
              type="primary"
              plain
              @click="edit(scope.row)"
              >View</el-button
            >
          </template>
        </el-table-column>
      </el-table>
    </div>
    <EditSubscription ref="editSubscription" />
  </div>
</template>

<script>
import EditSubscription from '@/components/products/manufacturers/EditSubscription'
import { subscriptionCodeStatus } from '../../../const'
import ApiService from '@/services/CommonApiService.js'
import loraTableHeight from '@/mixins/loraTableHeight'
import moment from 'moment'
import swal from 'sweetalert'
export default {
  mixins: [loraTableHeight],
  data() {
    return {
      moment,
      subscriptionCodeStatus,
      tableData: [],
      loading: false,
      addLoading: false,
      filter: '',
      timer: ''
    }
  },
  components: {
    EditSubscription
  },
  computed: {
    manufacturers() {
      return this.$store.state.manufacturer.manufacturers || []
    },
    safeManufacturers() {
      const list = this.manufacturers
      const sel = this.$store.state.manufacturer.selectedManufacturer
      if (sel && !list.find(m => m.id === sel.id)) return [sel, ...list]
      return list
    },
    selectedManufacturerId: {
      get() {
        const m = this.$store.state.manufacturer.selectedManufacturer
        return m ? m.id : null
      },
      set(id) {
        const m = this.manufacturers.find(x => x.id === id)
        if (m) this.$store.commit('manufacturer/SET_SELECTED_MANUFACTURER', m)
      }
    },
    selectedManufacturerName() {
      const m = this.$store.state.manufacturer.selectedManufacturer
      return m ? m.name : '--'
    }
  },
  mounted() {
    this.$store.dispatch('manufacturer/fetchManufacturer').then(() => {
      this.getData()
    })
    if (this.$parent.setPageAction) {
      this.$parent.setPageAction({
        label: 'Add Subscription',
        onClick: this.add
      })
    }
  },
  beforeDestroy() {
    if (this.$parent.clearPageAction) this.$parent.clearPageAction()
  },
  watch: {
    filter() {
      if (this.timer) clearTimeout(this.timer)
      this.timer = setTimeout(this.getData, 500)
    },
    '$store.state.manufacturer.selectedManufacturer'() {
      this.getData()
    }
  },
  methods: {
    statusTagType(status) {
      return (
        { Approved: 'success', Trial: 'warning', Disabled: 'info' }[status] ||
        'info'
      )
    },
    filterHandler(value, row, column) {
      return row[column.property] === value
    },
    getData() {
      if (!this.$store.state.manufacturer.selectedManufacturer) return
      this.loading = true
      ApiService.GetManufacturerCodesByManufacturerId(
        this.$store.state.manufacturer.selectedManufacturer.id
      )
        .then(response => {
          this.loading = false
          this.tableData = response.data.data.map(item => ({
            ...item,
            expiryDateTime:
              item.expiryDateTime === '0001-01-01T00:00:00'
                ? ''
                : moment
                    .utc(item.expiryDateTime)
                    .tz(moment.tz.guess(true))
                    .format('YYYY-MM-DD HH:mm:ss'),
            createdDateTime: moment
              .utc(item.createdDateTime)
              .tz(moment.tz.guess(true))
              .format('YYYY-MM-DD HH:mm:ss'),
            status: this.calculateStatus(item)
          }))
          if (this.filter) {
            this.tableData = this.tableData.filter(
              item =>
                item.code &&
                item.code.toLowerCase().indexOf(this.filter.toLowerCase()) > -1
            )
          }
        })
        .catch(() => {
          this.loading = false
        })
    },
    add() {
      if (!this.$store.state.manufacturer.selectedManufacturer) return
      this.addLoading = true
      ApiService.CreateManufacturerCode({
        manufacturerId: this.$store.state.manufacturer.selectedManufacturer.id
      })
        .then(response => {
          this.addLoading = false
          if (response.data.success) this.getData()
        })
        .catch(() => {
          this.addLoading = false
        })
    },
    approveCode(row) {
      this.loading = true
      ApiService.ApproveManufacturerCode({ id: row.id })
        .then(response => {
          this.loading = false
          if (response.data.success) {
            this.getData()
          } else {
            swal({
              icon: 'error',
              timer: 2000,
              title: this.$t('text.failed'),
              text: response.data.msg
            })
          }
        })
        .catch(() => {
          this.loading = false
          swal({ icon: 'error', timer: 2000, title: this.$t('text.failed') })
        })
    },
    edit(row) {
      this.$refs.editSubscription.openDialog()
      this.$refs.editSubscription.form = JSON.parse(JSON.stringify(row))
    },
    calculateStatus(item) {
      if (
        item.expiryDateTime !== '0001-01-01T00:00:00' &&
        moment.utc(item.expiryDateTime).isBefore(moment.utc())
      ) {
        return 'Disabled'
      }
      return item.isApproved ? 'Approved' : 'Trial'
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/theme.scss';

.list-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.manufacturer-select {
  width: 200px;
}

.list-filter {
  width: 200px;
}

.app-table {
  /deep/ .status-tag {
    border-radius: 99px;
    border: none;
    font-weight: 600;
  }
}
</style>
